/**
 * `<caos-detail-section>` — a labeled page-layout section holding a field grid.
 * (Catalog: Data display › DetailSection — one titled block of a record page, holding the fields that belong together.)
 *
 * A titled wrapper: a header carrying the section `heading`, an optional `read-only`
 * marker, and an `actions` slot on the right, above the default content slot (the
 * field grid) and an optional `footer` under it. When `collapsible`, the header becomes
 * a real `<button>` that toggles the content and reflects `aria-expanded`; a `collapsed`
 * attribute sets the initial (and reflects the current) state. A cancelable
 * `caos-section-toggle` event fires on change. Styling seams: `::part(section)`,
 * `::part(header)`, `::part(toggle)`, `::part(title)`, `::part(actions)`,
 * `::part(content)`, `::part(footer)`.
 *
 * CAOS-601 — the footer is adopted from the incumbent's card, which carries a footer and an
 * actions row on the same container. The actions row was already here; the footer is what a
 * section had no place for, and the absence showed: a summary under a field grid, a "3 of 12
 * shown" line under a list, a pair of buttons that belong to the whole block rather than to
 * its heading all had to be placed outside the card and stop looking like part of it. It folds
 * with the body, because a footer under a collapsed section is a stray line belonging to
 * nothing visible.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

const sheet = makeSheet(`
  :host { display: block; }

  /* Dragon CRM card (objectFramework CRM_CARD): a strong-bordered, softly elevated
     stacked-card record section — not a flat washed panel. */
  .section {
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border-strong);
    border-radius: var(--caos-radius-lg);
    box-shadow: var(--caos-shadow-card);
    overflow: hidden;
  }

  /* Dragon sectionHeading: a mono uppercase category header underlined by a hairline
     divider — the heavy filled header bar is dropped. The divider is INSET to the
     card's content padding (it stops at the 24px inset, not full-bleed), matching the
     Dragon detail-card treatment where the heading sits inside the padded card. */
  .header {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--caos-space-3);
    width: 100%;
    padding: var(--caos-space-4) var(--caos-space-5);
    background: transparent;
    border: none;
    text-align: left;
    color: var(--caos-color-text);
  }
  .header::after {
    content: '';
    position: absolute;
    left: var(--caos-space-5);
    right: var(--caos-space-5);
    bottom: 0;
    height: 1px;
    background: var(--caos-color-border);
  }
  /* the button variant (collapsible) is interactive */
  button.header { cursor: pointer; }
  button.header:hover { background: var(--caos-color-surface-2); }
  button.header:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .chevron {
    flex: none;
    width: 18px;
    height: 18px;
    color: var(--caos-color-text-muted);
    transition: transform 0.15s ease;
  }
  .chevron svg { display: block; width: 100%; height: 100%; }
  /* collapsed → chevron points right (rotate the down chevron -90deg) */
  :host([collapsed]) .chevron { transform: rotate(-90deg); }
  /* non-collapsible sections carry no toggle */
  :host(:not([collapsible])) .chevron { display: none; }

  .title {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--caos-font-mono);
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    line-height: 1.3;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .readonly {
    flex: none;
    display: none;
    align-items: center;
    gap: var(--caos-space-1);
    font-family: var(--caos-font-mono);
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .readonly svg { display: block; width: 12px; height: 12px; }
  :host([read-only]) .readonly { display: inline-flex; }

  .actions { flex: none; display: flex; align-items: center; gap: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }

  .content { padding: var(--caos-space-5); }
  :host([collapsed]) .content { display: none; }

  /* The footer mirrors the header: a hairline above it, inset to the same padding, so a card
     with both reads as one block divided twice rather than as three stacked things. It is not
     drawn at all when nothing is slotted into it — an empty strip with a rule above it is worse
     than no strip. */
  .footer {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-4) var(--caos-space-5);
  }
  .footer::before {
    content: '';
    position: absolute;
    left: var(--caos-space-5);
    right: var(--caos-space-5);
    top: 0;
    height: 1px;
    background: var(--caos-color-border);
  }
  .footer:not(:has(*)) { display: none; }
  :host([collapsed]) .footer { display: none; }
`);

export class CaosDetailSection extends CaosElement {
  static get observedAttributes(): string[] {
    return ['heading', 'collapsible', 'collapsed', 'read-only'];
  }

  private section: HTMLDivElement;
  private header!: HTMLElement;
  private titleEl!: HTMLSpanElement;
  private readonlyEl!: HTMLSpanElement;
  private content: HTMLDivElement;
  private footer: HTMLDivElement;
  private wasCollapsible: boolean | null = null;

  constructor() {
    super(sheet);

    this.section = document.createElement('div');
    this.section.className = 'section';
    this.section.setAttribute('part', 'section');

    this.content = document.createElement('div');
    this.content.className = 'content';
    this.content.setAttribute('part', 'content');
    this.content.append(document.createElement('slot'));

    this.footer = document.createElement('div');
    this.footer.className = 'footer';
    this.footer.setAttribute('part', 'footer');
    const footerSlot = document.createElement('slot');
    footerSlot.name = 'footer';
    this.footer.append(footerSlot);

    this.buildHeader();
    this.root.append(this.section);
  }

  /** (Re)build the header as a button (collapsible) or a plain div. */
  private buildHeader(): void {
    const collapsible = this.hasAttribute('collapsible');
    const el = document.createElement(collapsible ? 'button' : 'div') as HTMLElement;
    el.className = 'header';
    el.setAttribute('part', 'header');
    if (collapsible) {
      (el as HTMLButtonElement).type = 'button';
      el.setAttribute('part', 'header toggle');
      el.addEventListener('click', () => this.toggle());
    }

    const chevron = document.createElement('span');
    chevron.className = 'chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.append(makeIcon('chevron'));

    this.titleEl = document.createElement('span');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');

    this.readonlyEl = document.createElement('span');
    this.readonlyEl.className = 'readonly';
    this.readonlyEl.append(makeIcon('lock'), document.createTextNode('Read only'));

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionsSlot = document.createElement('slot');
    actionsSlot.name = 'actions';
    actions.append(actionsSlot);

    el.append(chevron, this.titleEl, this.readonlyEl, actions);
    this.header = el;
    this.section.replaceChildren(el, this.content, this.footer);
    this.wasCollapsible = collapsible;
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'collapsible' && this.wasCollapsible !== this.hasAttribute('collapsible')) {
      this.buildHeader();
    }
    this.sync();
  }

  /** Flip collapsed/expanded (only meaningful when collapsible); fires the event. */
  toggle(): void {
    const next = !this.hasAttribute('collapsed');
    const ev = new CustomEvent('caos-section-toggle', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { collapsed: next },
    });
    if (!this.dispatchEvent(ev)) return;
    if (next) this.setAttribute('collapsed', '');
    else this.removeAttribute('collapsed');
  }

  private sync(): void {
    this.titleEl.textContent = this.getAttribute('heading') ?? '';
    if (this.hasAttribute('collapsible')) {
      const expanded = !this.hasAttribute('collapsed');
      this.header.setAttribute('aria-expanded', String(expanded));
    } else {
      this.header.removeAttribute('aria-expanded');
    }
  }
}

define('caos-detail-section', CaosDetailSection);

declare global {
  interface HTMLElementTagNameMap {
    'caos-detail-section': CaosDetailSection;
  }
}
